import TimeLayer from "./TimeLayer";
import ImageLayer from "ol/layer/Image";
import ImageArcGISRest from "ol/source/ImageArcGISRest";
import Legend from "../Legend";

import moment from "moment-timezone";
// Services from NowCoast were discontinued. Use NoaaMapServicesBREF instead.

class NowCoastLayer extends TimeLayer {
  constructor(opt_options) {
    const options = opt_options || {};
    const baseOptions = Object.assign({}, options);
    delete baseOptions.service;
    delete baseOptions.layerid;
    super(baseOptions);

    this._service = options.service;
    this._layerids =
      typeof options.layerid === "number"
        ? options.layerid.toString()
        : options.layerid.join(",");

    this._last_timestop_check = null;
    this._poll_interval = moment.duration(30, "seconds");
    this._ongoing_poll = null;

    this._is_polling = false;
    this._polling_timerId = null;

    this.load = this.load.bind(this);
    this._map_times_updated = this._map_times_updated.bind(this);

    this._subscribedToTracking = false;

    this._legend_image = null;
    this._legend_pending = false;
  }

  load(timeManager) {
    this._poll().then(() => this._map_times_updated(timeManager));
  }

  async _poll(force = false) {
    const now = moment();
    if (
      force ||
      this._last_timestop_check === null ||
      now > this._last_timestop_check.clone().add(this._poll_interval)
    ) {
      if (!this._ongoing_poll) {
        this._ongoing_poll = fetch(
          "https://nowcoast.ncep.noaa.gov/layerinfo?request=timestops&service=" +
            this._service +
            "&layers=" +
            this._layerids +
            "&format=json"
        ).then(response => response.json());
      }
      try {
        const response = await this._ongoing_poll;
        let timeStops = [];
        response.layers.forEach(l => {
          timeStops = [...timeStops, ...l.timeStops];
        });
        this._timeStops = timeStops
          .filter((v, i, a) => a.indexOf(v) === i)
          .sort();
        this._last_timestop_check = moment();
      } catch (err) {
        console.warn("Error with nowcoast timestops");
      }
      this._ongoing_poll = null;
    }
    return;
  }

  _map_times_updated(timeManager) {
    const startTime = timeManager.start_date;
    const endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(moment.duration(15, "minutes"));
    // Filter to include only the range we care about.
    let addedOneEarly = false;
    let times = [];
    try {
      times = this._timeStops
        .sort()
        .reverse()
        .map(t => moment.tz(t, "UTC"))
        .filter(t => {
          if (
            (t >= startTime && t <= endTime) ||
            (t < startTime && t >= startTimeDelta && !addedOneEarly)
          ) {
            if (t < startTime) {
              addedOneEarly = true;
            }
            return true;
          }
          return false;
        });
    } catch (err) {
      console.error(err);
      times = [];
    }
    // Filter times down to those that don't already have layers (or have layers pending), then
    // create layers for any new times.
    const times_loaded = this.getLayers()
      .getArray()
      .map(l => l.get("time").valueOf());
    times
      .filter(t => times_loaded.indexOf(t.valueOf()) === -1)
      .forEach(t => {
        const newLayer = new ImageLayer({
          source: new ImageArcGISRest({
            ratio: 1.2,
            crossOrigin: "anonymous",
            params: {
              layers: "show:" + this._layerids,
              time: t.valueOf()
            },
            url:
              "https://nowcoast.ncep.noaa.gov/arcgis/rest/services/nowcoast/" +
              this._service +
              "/MapServer"
          }),
          time: t,
          title: `Nowcoast ${this._service} ${this._layerids}`
        });
        newLayer.set("legend", this._make_legend());
        this.getLayers().push(newLayer);
      });
  }

  _make_legend = () => {
    let waiter = null;
    if (!this._legend_image) {
      if (!this._legend_pending) {
        this._legend_pending = fetch(
          "https://nowcoast.ncep.noaa.gov/layerinfo?request=legend&service=" +
            this._service +
            "&layers=" +
            this._layerids +
            "&format=json"
        )
          .then(response => response.json())
          .then(response => {
            response = response[0];
            if (response.hasOwnProperty("url")) {
              this._legend_image = response.url.replace("http:", "https:");
            }
          })
          .catch(() => console.warn("Error with nowcoast legend service"));
      }
      waiter = this._legend_pending;
    } else {
      waiter = new Promise(res => res());
    }
    const legend = new Legend({
      draw: target => {
        if (!target.img) {
          waiter.then(() => {
            if (this._legend_image) {
              target.img = document.createElement("img");
              target.img.setAttribute("src", this._legend_image);
              target.img.setAttribute(
                "style",
                "margin:auto;display:block;max-width:100%"
              );
              target.container.appendChild(target.img);
            }
          });
        }
      },
      remove: target => {
        if (target.img) {
          target.container.removeChild(target.img);
        }
        target.img = null;
      }
    });
    return legend;
  };

  _schedule_poll = () => {
    this._poll().then(() => {
      if (this._is_polling) {
        this._polling_timerId = setTimeout(
          this._schedule_poll,
          this._poll_interval
        );
      }
    });
  };

  track_realtime(isTracking, timeManager) {
    if (!isTracking && this._is_polling) {
      if (this._polling_timerId !== null) {
        clearTimeout(this._polling_timerId);
        this._is_polling = false;
      }
    } else if (isTracking && !this._is_polling) {
      this._is_polling = true;
      this._schedule_poll();
    }
  }

  _realtime_data_received(newTime, newData) {
    this._map_times_received(
      { [newTime.valueOf()]: newData },
      this._timeManager
    );
  }
}

export default NowCoastLayer;
